import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useWallet } from '../hooks/useWallet';
import { useShippingMode } from '../context/ShippingModeContext';
import { LogOut, Globe, Truck } from 'lucide-react';
import RechargeModal from './Wallet/RechargeModal';

export default function Header({ setIsSidebarOpen }) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModeDropdownOpen, setIsModeDropdownOpen] = useState(false);
  const [isRechargeModalOpen, setIsRechargeModalOpen] = useState(false);
  const dropdownRef = useRef(null);
  const modeDropdownRef = useRef(null);
  const { user, logout } = useAuth();
  const { balance, fetchBalance } = useWallet();
  const { shippingMode, setShippingMode, SHIPPING_MODES } = useShippingMode();
  const [searchValue, setSearchValue] = useState("");
  const navigate = useNavigate();

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (modeDropdownRef.current && !modeDropdownRef.current.contains(event.target)) {
        setIsModeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (user?.name) {
      const names = user.name.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`.toUpperCase();
      }
      return user.name.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  // Format balance amount
  const formatBalance = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Handle recharge success
  const handleRechargeSuccess = () => {
    fetchBalance();
  };

  // handle ordre search
  const handleViewOrder = (orderId) => {
    if(!orderId.trim()){
      return
    }
    navigate(`/order-details?id=${orderId}`);
  };

  return (
    <nav className="flex items-center py-2 xl:py-3">
      {/* Left Section: Menu Icon */}
      <div className="flex-1 flex items-center">
        {/* Menu Icon */}
        <img src="/images/icon/toggle-menu.png" alt="toggle-menu" className="cursor-pointer w-6 lg:w-auto" onClick={() => setIsSidebarOpen(prev => !prev)} />
      </div>

      {/* Center Section: Search Bar */}
      <div className="flex-none px-2">
        {/* Search Input Group */}
        <div className="flex rounded-lg overflow-hidden">
          <div className="relative font-medium mr-1">
            <select className="p-2 bg-white text-[10px] lg:text-xs xl:text-sm font-semibold text-black appearance-none pr-5 2xl:pr-7 focus:outline-none h-10 cursor-pointer">
              <option>Order</option>
              <option>AWB</option>
            </select>
            {/* Custom Down Arrow */}
            <div className="pointer-events-none absolute inset-y-0 -right-1 flex items-center px-2 text-black">
              <svg className="w-3 xl:w-4 h-3 xl:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7"></path></svg>
            </div>
          </div>
          <input type="text" placeholder="Search multiple AWBs" className="w-32 lg:w-40 xl:w-52 text-black p-2 focus:outline-none bg-[#1318420D] placeholder:text-[10px] lg:placeholder:text-xs xl:placeholder:text-sm" value={searchValue} onChange={(e) => setSearchValue(e.target.value)} />
          <button className="py-2 px-2 xl:px-3 bg-white " onClick={() => handleViewOrder(searchValue)} >
            <svg className="w-5 xl:w-5 h-4 xl:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
          </button>
        </div>
      </div>

      {/* Right Section: Wallet, ID/Account, and Profile */}
      <div className="flex-1 flex items-center justify-end space-x-2 lg:space-x-3 xl:space-x-4">
        {/* Wallet Balance */}
        <div className="flex items-center space-x-2 xl:space-x-3">
          <div className="p-2 rounded-full bg-[#DC4D1C]">
            {/* Wallet Icon */}
            <img src="/images/icon/iconoir_wallet.png" alt="iconoir_wallet" className='w-4 xl:w-auto h-4 xl:h-auto' />
          </div>
          <div>
            <div className="text-[10px] lg:text-xs xl:text-sm text-[#131842] font-normal leading-5">Wallet Balance</div>
            <div className="font-semibold text-sm lg:text-base xl:text-lg text-gray-800 leading-5">
              {formatBalance(balance)}
            </div>
          </div>
          <button
            onClick={() => setIsRechargeModalOpen(true)}
            className="text-[#146BE6] hover:text-blue-600 text-[10px] lg:text-xs xl:text-sm font-semibold pl-2 cursor-pointer transition-colors"
          >
            Recharge
          </button>
        </div>

        {/* Divider */}
        <div className="h-12 w-px bg-gray-300"></div>

        {/* Account/ID Selector / Mode Switcher */}
        <div className="relative" ref={modeDropdownRef}>
          <div
            className="flex items-center space-x-2 xl:space-x-3 cursor-pointer"
            onClick={() => setIsModeDropdownOpen(!isModeDropdownOpen)}
          >
            <div className="p-2 rounded-full bg-white">
              {shippingMode === SHIPPING_MODES.INTERNATIONAL ? (
                <Globe className="w-4 xl:w-5 h-4 xl:h-5 text-blue-600" />
              ) : (
                <img src="/images/icon/mynaui_truck.png" alt="mynaui_truck" className='w-4 xl:w-auto h-4 xl:h-auto' />
              )}
            </div>
            <div>
              <div className="text-[10px] lg:text-xs xl:text-sm text-[#131842] leading-5">{user?.companyName || 'Flywell Logistics'}</div>
              <div className="font-semibold text-sm lg:text-base xl:text-lg text-gray-800 leading-5 capitalize">
                {shippingMode}
              </div>
            </div>
            <svg
              className={`w-4 xl:w-5 h-4 xl:h-5 text-[#146BE6] transition-transform duration-200 ${isModeDropdownOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7"></path>
            </svg>
          </div>

          {/* Mode Dropdown Menu */}
          {isModeDropdownOpen && (
            <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
              <div
                className={`px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-colors ${shippingMode === SHIPPING_MODES.DOMESTIC ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                onClick={() => {
                  setShippingMode(SHIPPING_MODES.DOMESTIC);
                  setIsModeDropdownOpen(false);
                  navigate('/dashboard');
                  // Give a small delay to let state update before reload if we really want a reload
                  setTimeout(() => window.location.reload(), 100);
                }}
              >
                <div className="p-1.5 rounded-md bg-gray-100">
                  <Truck className="w-4 h-4" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-bold">Domestic</span>
                  <span className="text-[10px] opacity-70">Ship within India</span>
                </div>
              </div>
              <div
                className={`px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-colors ${shippingMode === SHIPPING_MODES.INTERNATIONAL ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                onClick={() => {
                  setShippingMode(SHIPPING_MODES.INTERNATIONAL);
                  setIsModeDropdownOpen(false);
                  navigate('/dashboard');
                  // Give a small delay to let state update before reload if we really want a reload
                  setTimeout(() => window.location.reload(), 100);
                }}
              >
                <div className="p-1.5 rounded-md bg-gray-100">
                  <Globe className="w-4 h-4" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-bold">International</span>
                  <span className="text-[10px] opacity-70">Ship globally</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="h-12 w-px bg-gray-300"></div>

        {/* Profile Picture with Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-8 xl:w-10 h-8 xl:h-10 rounded-full overflow-hidden border-2 border-white shadow-md cursor-pointer hover:ring-2 hover:ring-blue-200 transition-all focus:outline-none"
          >
            {user?.name ? (
              <div className="w-full h-full bg-[#1a2b4b] flex items-center justify-center text-white font-bold text-xs xl:text-sm">
                {getUserInitials()}
              </div>
            ) : (
              <img src="/images/person-man.png" alt="Profile" className="object-cover w-full h-full" />
            )}
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
              <button
                onClick={handleLogout}
                className="w-full px-4 py-3 text-left text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-3"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Recharge Modal */}
      <RechargeModal
        isOpen={isRechargeModalOpen}
        onClose={() => setIsRechargeModalOpen(false)}
        onSuccess={handleRechargeSuccess}
      />
    </nav>
  );
}
